import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Empty,
  Space,
  Row,
  Col,
  Spin,
  Typography,
  Tag,
  Collapse,
  Button,
  Alert,
  Statistic,
  Divider,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { usePushViewContext } from "../../hooks/usePushView";
import PageHeader from "../../components/common/PageHeader";
import pushPriority from "../../services/pushPriority";
import { calculateStatusCounts } from "../../utils/statusCounts";

const { Text } = Typography;
const { Panel } = Collapse;

interface PushData {
  id: string;
  data: any;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  lastUpdate: Date | null;
  stats: {
    pending: number;
    warning: number;
    success: number;
    error: number;
    total: number;
  };
}

/**
 * Componente para visualizar um push individual e monitorar seu status.
 */
const PushViewer: React.FC<{ pushId: string }> = ({ pushId }) => {
  const [pushData, setPushData] = useState<PushData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Carrega os dados do push.
   */
  const loadPush = useCallback(async () => {
    try {
      setLoading(true);
      const response = await pushPriority.getDados({ cnjId: pushId });

      // Verifica se não foi encontrado
      if (!response || !response.success || !response.data) {
        setPushData({
          id: pushId,
          data: null,
          loading: false,
          error: "Push não encontrado",
          notFound: true,
          lastUpdate: new Date(),
          stats: {
            pending: 0,
            warning: 0,
            success: 0,
            error: 0,
            total: 0,
          },
        });
        // Para o monitoramento se não encontrado
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      const data = response.data;
      const info = data.info || [];

      // Calcula estatísticas (reutiliza helper comum)
      const stats = calculateStatusCounts(info, (item: any) => item.lastStatus);

      // Verifica se ainda está sendo capturado (tem pendentes)
      const isCapturing = stats.pending > 0;

      setPushData({
        id: pushId,
        data,
        loading: false,
        error: null,
        notFound: false,
        lastUpdate: new Date(),
        stats,
      });

      // Se ainda está sendo capturado, continua monitorando
      if (isCapturing) {
        // Se já existe um intervalo, não cria outro
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            loadPush();
          }, 60000); // 1 minuto
        }
      } else {
        // Se não está mais sendo capturado, para o monitoramento
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (err: any) {
      console.error("Erro ao buscar push:", err);

      setPushData({
        id: pushId,
        data: null,
        loading: false,
        error:
          err.response?.data?.message || err.message || "Erro ao buscar push",
        notFound: false,
        lastUpdate: new Date(),
        stats: {
          pending: 0,
          warning: 0,
          success: 0,
          error: 0,
          total: 0,
        },
      });

      // Para o monitoramento em caso de erro
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  }, [pushId]);

  useEffect(() => {
    loadPush();

    // Cleanup: limpa o intervalo quando o componente é desmontado
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loadPush]);

  if (loading && !pushData) {
    return (
      <Collapse>
        <Panel
          header={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <Text copyable code style={{ fontSize: "14px" }}>
                {pushId}
              </Text>
              <Spin size="small" />
            </div>
          }
          key={pushId}
          disabled
        >
          {/* Conteúdo vazio durante loading */}
        </Panel>
      </Collapse>
    );
  }

  if (!pushData) {
    return null;
  }

  if (pushData.notFound || pushData.error) {
    return (
      <Collapse>
        <Panel
          header={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <Text copyable code style={{ fontSize: "14px" }}>
                {pushId}
              </Text>
              <Tag color="error" icon={<CloseCircleOutlined />}>
                {pushData.notFound ? "Não encontrado" : "Erro"}
              </Tag>
              {pushData.error && !pushData.notFound && (
                <Text type="danger" style={{ marginLeft: 4 }}>
                  {pushData.error}
                </Text>
              )}
            </div>
          }
          key={pushId}
          disabled
        />
      </Collapse>
    );
  }

  const { stats, data, lastUpdate } = pushData;
  const isCapturing = stats.pending > 0;
  const info = data.info || [];
  const cnj = data.cnj || "N/A";

  return (
    <Collapse>
      <Panel
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <Text copyable code style={{ fontSize: "14px" }}>
                {pushId}
              </Text>
              <Tag color="cyan" style={{ marginLeft: 4 }}>
                {cnj}
              </Tag>
              {/* <Text type="secondary">Monitoramento</Text> */}
              {isCapturing ? (
                <Tag color="processing" icon={<ClockCircleOutlined />}>
                  Capturando...
                </Tag>
              ) : (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  Finalizado
                </Tag>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                justifyContent: "flex-end",
                minWidth: 220,
              }}
            >
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {/* <Tag color="blue" style={{ fontWeight: 600 }}>
                  {stats.total}
                </Tag> */}
                <Tag color="processing" style={{ fontWeight: 600 }}>
                  {stats.pending}
                </Tag>
                <Tag color="success" style={{ fontWeight: 600 }}>
                  {stats.success}
                </Tag>
                <Tag color="error" style={{ fontWeight: 600 }}>
                  {stats.error}
                </Tag>
                {stats.warning > 0 && (
                  <Tag color="warning" style={{ fontWeight: 600 }}>
                    {stats.warning}
                  </Tag>
                )}
              </div>
              {lastUpdate && (
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Última atualização: {lastUpdate.toLocaleTimeString()}
                </Text>
              )}
            </div>
          </div>
        }
        key={pushId}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <div>
            <div style={{ marginTop: 8 }}>
              {info.map((item: any, index: number) => {
                const status = item.lastStatus || "pending";
                const statusColor =
                  status === "success"
                    ? "success"
                    : status === "error"
                      ? "error"
                      : status === "warning"
                        ? "warning"
                        : "processing";

                // Abre automaticamente somente se for error (warning agora não abre)
                const shouldOpenByDefault = status === "error";

                return (
                  <Collapse
                    key={index}
                    style={{ marginBottom: 4 }}
                    defaultActiveKey={status === "error" ? [] : shouldOpenByDefault ? [index] : []}
                  >
                    <Panel
                      header={
                        <div style={{ width: "100%" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexWrap: "wrap",
                              marginBottom: status === "error" ? "8px" : "0",
                            }}
                          >
                            <Text copyable code style={{ fontSize: "14px" }}>
                              {item.id}
                            </Text>
                            <Tag color="cyan">{item.platformTag}</Tag>
                            <Tag color="volcano">{item.searchTag}</Tag>
                            <Tag color="purple">{item.queryTag}</Tag>
                            <Tag color={statusColor}>{status || "pending"}</Tag>
                            {(status === "warning" || status === "error") &&
                              item.message && (
                                <Text
                                  strong
                                  style={{
                                    marginLeft: 4,
                                    color:
                                      status === "warning"
                                        ? "#a56b00"
                                        : "#dc4446",
                                  }}
                                >
                                  {item.message}
                                </Text>
                              )}
                            <div
                              style={{ marginLeft: "auto", textAlign: "right" }}
                            >
                              {item.lastResponse && (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: "12px", display: "block" }}
                                >
                                  Última resposta:{" "}
                                  {new Date(item.lastResponse).toLocaleString()}
                                </Text>
                              )}
                            </div>
                          </div>
                        </div>
                      }
                      key={index}
                      disabled={status === "warning" || status === "error"}
                    >
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: "100%" }}
                      >
                        
                        {/* Dados de Success */}
                        {status === "success" &&
                          item.data &&
                          Array.isArray(item.data) &&
                          item.data.length > 0 && (
                            <div>
                              <Text strong type="success">
                                Dados do Request (Success):
                              </Text>
                              <div
                                style={{
                                  marginTop: 8,
                                  padding: 12,
                                  background: "#0f1724",
                                  border: "1px solid #233044",
                                  borderRadius: 4,
                                }}
                              >
                                <div
                                  style={{
                                    maxHeight: "500px",
                                    overflow: "auto",
                                  }}
                                >
                                  <pre
                                    style={{
                                      margin: 0,
                                      fontSize: "12px",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                      color: "rgba(255,255,255,0.92)",
                                    }}
                                  >
                                    {JSON.stringify(item.data, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          )}

                        {/* Dados de Success (formato diferente) */}
                        {status === "success" &&
                          item.data &&
                          !Array.isArray(item.data) && (
                            <div>
                              <Text strong type="success">
                                Dados do Request (Success):
                              </Text>
                              <div
                                style={{
                                  marginTop: 8,
                                  padding: 12,
                                  background: "#f6ffed",
                                  border: "1px solid #b7eb8f",
                                  borderRadius: 4,
                                }}
                              >
                                <div
                                  style={{
                                    maxHeight: "500px",
                                    overflow: "auto",
                                  }}
                                >
                                  <pre
                                    style={{
                                      margin: 0,
                                      fontSize: "12px",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {JSON.stringify(item.data, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          )}

                        {/* Pendente */}
                        {status === "pending" && (
                          <div>
                            <Text type="secondary">Aguardando captura...</Text>
                          </div>
                        )}

                        {/* Sem dados */}
                        {!item.data &&
                          !item.message &&
                          status !== "pending" && (
                            <div>
                              <Text type="secondary">
                                Nenhum dado disponível
                              </Text>
                            </div>
                          )}
                      </Space>
                    </Panel>
                  </Collapse>
                );
              })}
            </div>
          </div>

          <Divider style={{ margin: "8px 0" }} />

          {/* Dados completos do último GET (para debug) */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <Text strong>Resultado completo do último GET:</Text>
              <Button
                type="default"
                size="small"
                icon={<CopyOutlined />}
                onClick={async () => {
                  try {
                    const jsonString = JSON.stringify(data, null, 2);
                    await navigator.clipboard.writeText(jsonString);
                    message.success(
                      "Conteúdo copiado para a área de transferência!",
                    );
                  } catch (err) {
                    console.error("Erro ao copiar:", err);
                    message.error("Erro ao copiar conteúdo");
                  }
                }}
              >
                Copiar
              </Button>
            </div>
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: "#0b1220",
                border: "1px solid #233044",
                borderRadius: 4,
              }}
            >
              <div style={{ maxHeight: "400px", overflow: "auto" }}>
                <pre
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: "rgba(255,255,255,0.92)",
                  }}
                >
                  {JSON.stringify(data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </Space>
      </Panel>
    </Collapse>
  );
};

/**
 * Página de monitoramento de pushes.
 *
 * Exibe todos os pushes que estão sendo monitorados e atualiza a cada 1 minuto.
 */
const PushVisualizarMonitoringContent: React.FC = () => {
  const { pushIds } = usePushViewContext();
  const navigate = useNavigate();
  const [globalStats, setGlobalStats] = useState({
    pending: 0,
    warning: 0,
    success: 0,
    error: 0,
    total: 0,
  });

  // Recalcular estatísticas globais quando os dados mudarem
  useEffect(() => {
    // As estatísticas serão calculadas individualmente por cada PushViewer
    // Aqui podemos adicionar lógica para agregar se necessário
  }, [pushIds]);

  const handleBack = () => {
    navigate("/push/visualizar");
  };

  const handleRefresh = () => {
    // Forçar reload de todos os componentes
    window.location.reload();
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageHeader title="Monitoramento de Pushes" />
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              Atualizar
            </Button>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Voltar
            </Button>
          </Space>
        </div>

        <Alert
          message="Monitoramento Ativo"
          description="Os pushes estão sendo monitorados a cada 1 minuto. Pushes que ainda estão sendo capturados continuarão sendo monitorados até que todas as abrangências sejam finalizadas."
          type="info"
          showIcon
        />

        {pushIds.length === 0 ? (
          <Empty
            description="Nenhum push sendo monitorado. Volte para a página de Visualizar Vários Pushes para adicionar."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {pushIds.map((pushId, idx) => (
              <Col xs={24} sm={24} md={24} lg={24} xl={24} key={idx}>
                <PushViewer pushId={pushId} />
              </Col>
            ))}
          </Row>
        )}
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página PushVisualizarMonitoring.
 */
const PushVisualizarMonitoring: React.FC = () => {
  return (
    <PushVisualizarMonitoringContent />
  );
};

export default PushVisualizarMonitoring;
